// Crew availability slots for the booking: the colours, the "can this slot hold the whole
// job" rules and what a click on a slot does. Moved out of Bookings.jsx unchanged (the
// logic is subtle - one crew member's click must never wipe another's selection) so it can
// be tested; the stepper only wires the results to state.
//
// Slot status codes: 1 available, 2 selected (pending) for this booking, 3 booked,
// 6 break, 99 holiday.

export function getStatusColors(status) {
  const statusColors = {
    1: { bgColor: "#F2FFF0", textColor: "#666600" },
    2: { bgColor: "#FFF0FD", borderColor: "#92007E", textColor: "#92007E" },
    3: { bgColor: "#FDEDED", borderColor: "#FF0000", textColor: "#FF0000" },
    4: { bgColor: "#28a745", borderColor: "#FF0000", textColor: "#FF0000" },
    6: { bgColor: "#FDEDED", borderColor: "#FF0000", textColor: "#FF0000" },
    10: { bgColor: "#FDEDED", borderColor: "#FF0000", textColor: "#FF0000" },
    11: { bgColor: "#FFF0FD", borderColor: "#92007E", textColor: "#92007E" },
    14: { bgColor: "rgba(255, 204, 204, 0.5)", borderColor: "#CC0000", textColor: "#990000" },
    12: { bgColor: "F2F9FF", borderColor: "#037FE8", textColor: "#037FE8" },
    15: { bgColor: "rgba(255, 255, 204, 0.5)", borderColor: "#CCCC00", textColor: "#666600" },
    99: { bgColor: "#FFE5E5", borderColor: "#FF4D4D", textColor: "#B30000" },
    default: { bgColor: "rgba(230, 230, 230, 0.5)", borderColor: "#999999", textColor: "#666666" }
  };
  return statusColors[status] || statusColors.default;
}

// 2026-10-09T14:30:00 (local wall clock, no zone)
export function convertWithoutTimseStamp(date) {
  try {
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1).padStart(2, "0");
    const day = String(date?.getDate()).padStart(2, "0");
    const hours = String(date?.getHours()).padStart(2, "0");
    const minutes = String(date?.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  } catch (e) {
    console.log("err---", date, e);
  }
}

// Within one crew member, drop the free slots that overlap a slot booked for this booking.
export function cleanCrewSlots(slots) {
  const grouped = {};
  slots.forEach((slot) => {
    const id = slot.extendedProps.provider_id;
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(slot);
  });

  const result = [];
  Object.values(grouped).forEach((group) => {
    const bookedSlots = group.filter((s) => s.status === 2);
    const cleaned = group.filter((slot) => {
      if (slot.status === 2) return true;
      const isOverlapping = bookedSlots.some(
        (booked) => new Date(slot.start) < new Date(booked.end) && new Date(slot.end) > new Date(booked.start)
      );
      return !isOverlapping;
    });
    result.push(...cleaned);
  });
  return result;
}

export function hasOverlapWithBooked(slot, proposedEnd, bookedSlots) {
  const slotStart = new Date(slot.start);
  for (const booked of bookedSlots) {
    if (booked.extendedProps?.provider_id !== slot.extendedProps?.provider_id) continue;
    const bookedStart = new Date(booked.start);
    const bookedEnd = new Date(booked.end);
    // proposed window straddles or overlaps the booked window
    const endLandsInside = proposedEnd > bookedStart && proposedEnd < bookedEnd;
    const startBeforeEnd = slotStart < bookedStart && proposedEnd > bookedEnd;
    if (endLandsInside || startBeforeEnd) return true;
  }
  return false;
}

// True when the slot overlaps any break slot for the same provider.
export function hasOverlapWithBreaks(slot, proposedEnd, breakSlots) {
  const slotStart = new Date(slot.start);
  for (const brk of breakSlots) {
    if (brk.extendedProps?.provider_id !== slot.extendedProps?.provider_id) continue;
    const breakStart = new Date(brk.start);
    const breakEnd = new Date(brk.end);
    // Only an actual overlap between [slotStart, proposedEnd) and [breakStart, breakEnd)
    // blocks selection - a break earlier (or later) that the window doesn't touch must not.
    if (slotStart < breakEnd && proposedEnd > breakStart) return true;
  }
  return false;
}

// True when the proposed end is later than the provider's last available slot.
export function exceedsProviderAvailability(appointment, proposedEnd, allSlots) {
  const providerId = appointment.details?.extendedProps?.provider_id;
  const maxProviderEnd = allSlots
    .filter((s) => s.extendedProps?.provider_id === providerId)
    .map((s) => new Date(s.end))
    .reduce((max, curr) => (curr > max ? curr : max), new Date(0));
  return proposedEnd > maxProviderEnd;
}

// First start / last end of a slot list, as the calendar's visible hours.
export function computeTimeRange(slots) {
  const { minStart, maxEnd } = slots.reduce(
    (acc, s) => ({
      minStart: s.start < acc.minStart ? s.start : acc.minStart,
      maxEnd: s.end > acc.maxEnd ? s.end : acc.maxEnd
    }),
    { minStart: slots[0].start, maxEnd: slots[0].end }
  );
  const rawStart = minStart?.split?.("T")?.[1];
  const rawEnd = maxEnd?.split?.("T")?.[1];
  return {
    startTime: !rawStart || rawStart.includes("NaN") ? "08:00:00" : rawStart,
    endTime: !rawEnd || rawEnd.includes("NaN") ? "20:00:00" : rawEnd
  };
}

/**
 * What a click on a calendar slot does.
 *
 * Returns { ignored: true } when nothing should change, otherwise a patch:
 *   dateSelect          the clicked day (always set once past the guards)
 *   message             "Not enough availability..." when the job doesn't fit (may be set together with a patch)
 *   providerSelected    the crew now holding a slot for this booking
 *   filteredAppointments / timeRange   what the calendar shows next (absent when no slot is left to show)
 */
export function resolveSlotClick({ appointment, filteredAppointments, initialAppointments, maxTime, crewCount }) {
  // Holidays and already-blocked slots are not selectable.
  if (appointment?.details?.extendedProps?.isHoliday) return { ignored: true };
  if ([3, 6].includes(appointment?.details?.status)) return { ignored: true };

  const slots = JSON.parse(JSON.stringify(filteredAppointments));
  const bookedSlots = slots.filter((s) => s.status === 3 || s.status === 6);
  const breakSlots = slots.filter((s) => s.status === 6);

  const clickedStart = appointment.details?.start;
  const clickedProviderId = appointment.details?.extendedProps?.provider_id;
  const slotDuration = maxTime / (crewCount + 1); // minutes

  const patch = { dateSelect: clickedStart?.split("T")?.[0] };

  // Nothing to resolve if the clicked slot is already booked.
  if (appointment.details?.status === 3) {
    patch.providerSelected = [];
    return patch;
  }

  const agentAssigned = [];

  for (const slot of slots) {
    // Never touch booked / break slots
    if ([3, 6].includes(slot.status)) continue;

    const sameStart = slot.start === clickedStart;
    const sameProvider = slot.extendedProps?.provider_id === clickedProviderId;

    if (!sameProvider) {
      // A different crew member's slot - leave their existing selection (2) or
      // availability (1) alone, or selecting a second crew member at another time would
      // wipe out whoever was already selected.
      if (slot.status === 2) agentAssigned.push(slot);
      continue;
    }

    if (!sameStart) {
      // Same provider, different time -> clear their previous selection (one active slot each)
      slot.status = 1;
      const end = new Date(slot.start);
      end.setMinutes(end.getMinutes() + 30);
      slot.end = convertWithoutTimseStamp(end); // local text, like every other slot (a Date becomes UTC text when copied)
      continue;
    }

    if (sameStart && sameProvider && slot.status === 2) {
      // Same time, same provider, currently selected -> revert to available
      slot.status = 1;
      const end = new Date(slot.start);
      end.setMinutes(end.getMinutes() + 30);
      slot.end = convertWithoutTimseStamp(end); // local text, like every other slot (a Date becomes UTC text when copied)
      continue;
    }

    if (sameStart && sameProvider && slot.status === 1) {
      // The slot being selected -> validate, then mark as selected (2)
      const proposedEnd = new Date(slot.start);
      proposedEnd.setMinutes(proposedEnd.getMinutes() + slotDuration);

      const durationHours = slotDuration / 60;
      const insufficientMsg =
        `Not enough availability. This slot requires a full ${durationHours}-hour window. ` +
        `Please select a different start time.`;

      if (hasOverlapWithBooked(slot, proposedEnd, bookedSlots)) {
        patch.message = insufficientMsg;
        continue;
      }
      if (hasOverlapWithBreaks(slot, proposedEnd, breakSlots)) {
        patch.message = insufficientMsg;
        continue;
      }
      if (exceedsProviderAvailability(appointment, proposedEnd, slots)) {
        patch.message = insufficientMsg;
        continue;
      }

      slot.status = 2;
    }

    if (slot.status === 2) agentAssigned.push(slot);
  }

  // Merge the slot statuses back into the master list.
  const merged = JSON.parse(JSON.stringify(initialAppointments));
  for (let i = 0; i < merged.length; i++) {
    // Apply whatever status the slot ended up with - not just a newly selected one - or a
    // slot toggled back to available would stay stuck as selected.
    const match = slots.find(
      (s) => s.start === merged[i].start && s.extendedProps?.provider_id === merged[i].extendedProps?.provider_id
    );
    if (match) merged[i] = JSON.parse(JSON.stringify(match));
  }

  patch.providerSelected = agentAssigned;

  // The crew work in parallel: each holds the job's total time divided between them.
  const timePerAgent = agentAssigned.length > 0 ? maxTime / agentAssigned.length : maxTime;
  for (const agent of agentAssigned) {
    if (agent.status === 2) {
      const end = new Date(agent.start);
      end.setMinutes(end.getMinutes() + timePerAgent);
      agent.end = convertWithoutTimseStamp(end);
    }
  }

  // The visible slot list: selected / booked slots always, free ones unless they sit inside
  // an agent's window.
  const visible = [];
  for (const slot of merged) {
    if ([2, 3].includes(slot.status)) {
      visible.push(slot);
      continue;
    }
    const hiddenByAgent = agentAssigned.some((agent) => {
      const slotStart = convertWithoutTimseStamp(new Date(slot.start));
      const agentStart = convertWithoutTimseStamp(new Date(agent.start));
      const agentEnd = convertWithoutTimseStamp(new Date(agent.end));
      return (
        slot.extendedProps?.provider_id === agent.extendedProps?.provider_id &&
        slotStart > agentStart &&
        slotStart < agentEnd
      );
    });
    if (hiddenByAgent) continue;
    visible.push(slot);
  }

  for (const slot of visible) {
    if (slot.status === 2) {
      const end = new Date(slot.start);
      end.setMinutes(end.getMinutes() + timePerAgent);
      slot.end = convertWithoutTimseStamp(end);
    }
  }

  const cleaned = cleanCrewSlots(visible);
  if (!cleaned || cleaned.length === 0) {
    console.warn("resolveSlotClick: no visible slots after clean");
    return patch;
  }

  patch.timeRange = computeTimeRange(cleaned);
  patch.filteredAppointments = cleaned;
  return patch;
}

/**
 * Turns one day's raw slots into what the calendar shows (the non-network half of
 * getWorkerAvailableSlots).
 *   raw               getWokerAvailableSlots response data
 *   availabilityMap   { [providerId]: ['YYYY-MM-DD', ...] } crew holidays
 *   day               'YYYY-MM-DD' being loaded
 *   providerSelected  crew already selected for this booking (kept as selected, hide their window)
 */
export function buildDaySlots({ raw, availabilityMap, day, providerSelected, maxTime, crewCount }) {
  const updatedData = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : [];
  const map = availabilityMap || {};
  const hasMap = Object.keys(map).length > 0;

  const isCrewOnHoliday = (providerId, dateTime) => {
    if (!providerId || !dateTime) return false;
    if (!hasMap) return false;
    const date = dateTime.split("T")[0];
    return map?.[providerId]?.includes(date) || false;
  };

  // Re-apply the selection on the fresh slots.
  for (const i of providerSelected || []) {
    for (const j of updatedData) {
      if (i?.start === j?.start && i?.extendedProps?.provider_id === j?.extendedProps?.provider_id) {
        j.status = 2;
        const date = new Date(j.start);
        date.setMinutes(date.getMinutes() + (maxTime || 0));
        j.end = date;
      }
    }
  }

  const holidayCrewIds = new Set();
  if (hasMap) {
    Object.keys(map).forEach((providerId) => {
      if (map[providerId]?.includes(day)) holidayCrewIds.add(providerId);
    });
  }

  const updated = [];
  for (const i of updatedData) {
    if (!i?.start || !i?.end) continue;

    const providerId = i?.extendedProps?.provider_id;
    const isHoliday = isCrewOnHoliday(providerId, i.start);

    i.extendedProps = i.extendedProps || {};
    i.extendedProps.isHoliday = isHoliday;

    if (isHoliday) {
      i.status = 99;
      i.extendedProps.isDisabled = true;
    }

    let flag = true;

    if ([2, 3].includes(i.status)) {
      updated.push(i);
      continue;
    }

    for (const j of providerSelected || []) {
      if (!j?.start || !j?.end) continue;
      const s1 = new Date(i.start);
      const e1 = new Date(i.end);
      const s2 = new Date(j.start);
      const e2 = new Date(j.end);
      const p1 = providerId;
      const p2 = j?.extendedProps?.provider_id;
      if (p1 === p2 && s1 >= s2 && e1 <= e2) {
        flag = false;
        break;
      }
    }

    if (flag) updated.push(i);
  }

  for (const i of updated) {
    if (i.status === 2) {
      const date = new Date(i.start);
      const t1 = (maxTime || 0) / (crewCount || 1);
      date.setMinutes(date.getMinutes() + t1);
      i.end = convertWithoutTimseStamp(date);
    }
  }

  const slots = cleanCrewSlots(updated || []);
  return {
    // The same (mutated) array the old code kept as initialAppointments - the click handler merges into it.
    initialAppointments: updatedData,
    slots,
    holidayCrews: [...holidayCrewIds],
    timeRange: slots.length ? computeTimeRange(slots) : { startTime: "08:00:00", endTime: "20:00:00" }
  };
}
